// src/utils/compressImage.js
export async function compressImageFile(file, {
    maxWidth = 1280,
    maxSizeKB = 220,
    initialQuality = 0.82,
    minQuality = 0.45,
} = {}) {
    if (!file) throw new Error("No file");

    const dataUrl = await fileToDataURL(file);
    const img = await dataURLToImage(dataUrl);

    // resize
    const { canvas, ctx } = makeCanvas(img, maxWidth);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // iterative quality
    let quality = initialQuality;
    let out = canvas.toDataURL("image/jpeg", quality);

    while (dataUrlSizeKB(out) > maxSizeKB && quality > minQuality) {
        quality -= 0.07;
        out = canvas.toDataURL("image/jpeg", quality);
    }

    return out; // dataURL jpeg
}

function fileToDataURL(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
    });
}

function dataURLToImage(dataUrl) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = dataUrl;
    });
}

function makeCanvas(img, maxWidth) {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const ratio = img.width > maxWidth ? (maxWidth / img.width) : 1;
    canvas.width = Math.round(img.width * ratio);
    canvas.height = Math.round(img.height * ratio);

    return { canvas, ctx };
}

function dataUrlSizeKB(dataUrl) {
    // approx base64 size
    const head = "data:image/jpeg;base64,";
    const b64 = dataUrl.startsWith(head) ? dataUrl.slice(head.length) : dataUrl;
    const bytes = Math.ceil((b64.length * 3) / 4);
    return bytes / 1024;
}

const readArrayBuffer = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("Gagal membaca fail imej"));
    reader.readAsArrayBuffer(blob);
});

const sourceAssetId = async (file) => {
    const bytes = new Uint8Array(await readArrayBuffer(file));
    let hash = 2166136261;
    for (const byte of bytes) {
        hash ^= byte;
        hash = Math.imul(hash, 16777619);
    }
    return `img-${(hash >>> 0).toString(16).padStart(8, "0")}-${bytes.length}`;
};

const loadObjectImage = (file) => new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(image);
    };
    image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Imej rosak atau format tidak disokong"));
    };
    image.src = objectUrl;
});

const canvasBlob = (canvas, quality) => new Promise((resolve, reject) => {
    canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error("Gagal memproses imej")),
        "image/jpeg",
        quality
    );
});

const renderVariant = async (file, { maxWidth, maxBytes }) => {
    const image = await loadObjectImage(file);
    let width = Math.min(image.naturalWidth || image.width, maxWidth);
    let height = Math.round(width * (image.naturalHeight || image.height) /
        (image.naturalWidth || image.width));
    let quality = 0.84;
    let blob;

    for (let attempt = 0; attempt < 10; attempt += 1) {
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(width));
        canvas.height = Math.max(1, Math.round(height));
        const context = canvas.getContext("2d");
        if (!context) throw new Error("Pelayar tidak menyokong pemprosesan imej");
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        blob = await canvasBlob(canvas, quality);
        width = canvas.width;
        height = canvas.height;
        if (blob.size <= maxBytes) break;
        if (quality > 0.48) quality -= 0.08;
        else {
            width = Math.max(1, Math.round(width * 0.85));
            height = Math.max(1, Math.round(height * 0.85));
        }
    }

    if (!blob || blob.size > maxBytes) {
        throw new Error("Imej tidak dapat dikecilkan ke saiz selamat");
    }
    return { blob, width, height };
};

export async function createImageVariants(file, { transform = renderVariant } = {}) {
    if (!file || !String(file.type || "").startsWith("image/")) {
        throw new TypeError("Sila pilih fail imej yang sah");
    }
    if (file.size > 15 * 1024 * 1024) {
        throw new RangeError("Fail imej melebihi had 15 MB");
    }

    const [assetId, thumbnail, medium] = await Promise.all([
        sourceAssetId(file),
        transform(file, { maxWidth: 480, maxBytes: 100 * 1024 }),
        transform(file, { maxWidth: 1280, maxBytes: 300 * 1024 }),
    ]);

    if (thumbnail.blob.size > 100 * 1024 || medium.blob.size > 300 * 1024) {
        throw new Error("Varian imej melebihi saiz selamat");
    }
    const thumbnailRatio = thumbnail.width / thumbnail.height;
    const mediumRatio = medium.width / medium.height;
    if (!Number.isFinite(thumbnailRatio) || !Number.isFinite(mediumRatio) ||
        Math.abs(thumbnailRatio - mediumRatio) > 0.01) {
        throw new Error("Nisbah imej berubah semasa pemprosesan");
    }

    return {
        assetId,
        thumbnail,
        medium,
        original: file,
        originalWidth: medium.sourceWidth || medium.width,
        originalHeight: medium.sourceHeight || medium.height,
    };
}
